import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogTitle, 
  IconButton,
  Box,
  Typography
} from '@mui/material';
import { DataGrid, GridPagination } from '@mui/x-data-grid';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

const MonthlyCallReport = ({ callLogs, showMonthlyReport, setShowMonthlyReport }) => {
  const [reportData, setReportData] = useState([]);

  useEffect(() => {
    generateMonthlyReport();
  }, [callLogs]);

  const generateMonthlyReport = () => {
    const now = new Date();
    const reports = [];

    // Generate last 13 months (current month + 12 previous)
    for (let i = 0; i < 13; i++) {
      const monthDate = subMonths(now, i);
      const monthStart = startOfMonth(monthDate);
      const monthEnd = endOfMonth(monthDate);

      // Initialize counters
      const counts = {
        morning: 0,    // 6am-12pm
        afternoon: 0,  // 12pm-6pm
        evening: 0,    // 6pm-12am
        night: 0       // 12am-6am
      };

      // Count calls for this month
      callLogs.forEach(call => {
        const callDate = new Date(call.callDate);
        
        // Check if call is in this month
        if (callDate >= monthStart && callDate <= monthEnd) {
          const hour = callDate.getHours();

          if (hour >= 6 && hour < 12) counts.morning++;
          else if (hour >= 12 && hour < 18) counts.afternoon++;
          else if (hour >= 18 && hour < 24) counts.evening++;
          else counts.night++;
        }
      });

      const total = counts.morning + counts.afternoon + counts.evening + counts.night;

      reports.push({
        id: i,
        month: format(monthDate, 'MMM yyyy'),
        monthDate: monthDate,
        morning: counts.morning,
        afternoon: counts.afternoon,
        evening: counts.evening,
        night: counts.night,
        total: total
      });
    }

    setReportData(reports);
  };

  const handleClose = () => {
    setShowMonthlyReport(false);
  };

  const columns = [
    {
      field: 'month',
      headerName: 'Month',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Typography fontWeight={params.row.id === 0 ? 700 : 500}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'morning',
      headerName: 'Morning (6am-12pm)',
      width: 180,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Typography>{params.value}</Typography>
      )
    },
    {
      field: 'afternoon',
      headerName: 'Afternoon (12pm-6pm)',
      width: 180,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Typography>{params.value}</Typography>
      )
    },
    {
      field: 'evening',
      headerName: 'Evening (6pm-12am)',
      width: 180,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Typography>{params.value}</Typography>
      )
    },
    {
      field: 'night',
      headerName: 'Night (12am-6am)',
      width: 180,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Typography>{params.value}</Typography>
      )
    },
    {
      field: 'total',
      headerName: 'Total Calls',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Typography fontWeight={600} color="primary">
          {params.value}
        </Typography>
      )
    }
  ];

  // Calculate totals for all data
  const totals = reportData.reduce(
    (acc, row) => ({
      morning: acc.morning + row.morning,
      afternoon: acc.afternoon + row.afternoon,
      evening: acc.evening + row.evening,
      night: acc.night + row.night,
      total: acc.total + row.total
    }),
    { morning: 0, afternoon: 0, evening: 0, night: 0, total: 0 }
  );

  function CustomPagination(props) {
    return (
      <>
        <b>Morning: {totals.morning}</b>
        <b style={{ marginLeft: '15px' }}>Afternoon: {totals.afternoon}</b>
        <b style={{ marginLeft: '15px' }}>Evening: {totals.evening}</b>
        <b style={{ marginLeft: '15px' }}>Night: {totals.night}</b>
        <b style={{ marginLeft: '15px', color: '#1976d2' }}>Total: {totals.total}</b>
        <b style={{ marginLeft: '15px', color: '#1976d2' }}></b>

        {/* <GridPagination {...props} /> */}
      </>
    );
  }

  return (
    <Dialog 
      open={showMonthlyReport} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        Monthly Call Report (Last 13 Months)
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          ✕
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={reportData}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            disableRowSelectionOnClick
            disableColumnMenu
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                fontWeight: 700,
                fontSize: '0.95rem'
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #e0e0e0'
              },
              '& .MuiDataGrid-row:nth-of-type(odd)': {
                backgroundColor: '#fafafa'
              },
              '& .MuiDataGrid-row:first-of-type': {
                backgroundColor: '#e3f2fd',
                fontWeight: 600
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '2px solid #1976d2',
                backgroundColor: '#f5f5f5'
              }
            }}
            slots={{
                  pagination: CustomPagination,
                }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MonthlyCallReport;